import {DebuggerMode, Mode} from '../../../shared/DebuggerMode'
import {DebuggerConsole} from '../../../shared/DebuggerConsole'

type CodeToNameMap = typeof CodeToNameMap
type NameToCodeMap = {[key in CodeToNameMap[keyof CodeToNameMap]]: string}

/** https://stat.yandex-team.ru/DictionaryEditor/BlockStat */
const CodeToNameMap = {
  '32': 'event',
  '207': 'time',
  '2096': 'perf',
  '2892': 'navigation',
  '690': 'tech',
  '1033': 'timing',
  '4004': 'cls',
  '361': 'traffic',
  '2877': 'delta',
  '2876': 'hero',
  '2154': 'start',
  '2095': 'main_js',
  '3872': 'react_inited',
  '2354': 'client_error',
  '2361': 'uncaught_error',
  '2609': 'script_error',
  '2854': 'external_error'
} as const

const NameToCodeMap = Object.fromEntries(Object.entries(CodeToNameMap).map(([key, val]) => [val, key])) as NameToCodeMap

try {
  const mode = new DebuggerMode({key: '_rum_debug'}).getMode()

  switch (mode) {
    case Mode.ENABLED_COLLAPSE:
    case Mode.ENABLED: {
      const originalSend = window.Ya.Rum.send

      window.Ya.Rum.send = (...args) => {
        try {
          logSendCall(args, Mode.ENABLED_COLLAPSE === mode)
        } catch (error) {
          console.error(error)
        }

        originalSend(...args)
      }

      break
    }

    default: {
      // noop
    }
  }
} catch (error) {
  console.error(error)
}

function logSendCall(args: Parameters<typeof window.Ya.Rum.send>, collapse?: boolean) {
  const varsRaw = args[2] || ''
  const vars = Object.fromEntries(
    varsRaw
      .split(',')
      .map((value) => value.split('=') as [string, string])
      .map(([key, value]) => [key, decodeURIComponent(value)])
  )

  try {
    vars['-additional'] = JSON.parse(vars['-additional'] || '{}')
  } catch {
    // noop
  }

  const pathRaw = args[1] || ''

  const isEvent = pathRaw.startsWith(`${NameToCodeMap.tech}.${NameToCodeMap.event}`)
  const isError = [
    NameToCodeMap.client_error,
    NameToCodeMap.script_error,
    NameToCodeMap.uncaught_error,
    NameToCodeMap.external_error
  ].some((val) => pathRaw.startsWith(`${NameToCodeMap.tech}.${val}`))
  const isRum = !isEvent && !isError
  const logType = isError ? 'err' : isEvent ? 'evt' : isRum ? 'rum' : 'nil'

  const groupName = `${logType}:${vars['-service'] || 'unknown_service'}`

  const badgeColorMap: Record<typeof logType, string> = {
    nil: 'red',
    rum: 'hsl(246deg, 50%, 50%)',
    err: 'hsl(9deg, 80%, 60%)',
    evt: 'hsl(180deg, 40%, 30%)'
  }

  const debuggerConsole = new DebuggerConsole({
    badge: 'clck',
    badgeColor: '#fff',
    badgeBgColor: badgeColorMap[logType],
    collapse
  })

  const defaultLogName = parsePath(pathRaw)

  switch (logType) {
    case 'evt': {
      debuggerConsole.createGroup(groupName, `${vars['-name'] || 'no_name'} ${JSON.stringify(vars['-value'])}`)
      break
    }

    case 'err': {
      debuggerConsole.createGroup(groupName, `${defaultLogName} ${vars['-msg'] || ''}`).log(vars['-stack'])
      break
    }

    default: {
      debuggerConsole.createGroup(groupName, `${defaultLogName}`)
      break
    }
  }

  debuggerConsole.log(vars).endGroup()
}

function parsePath(path: string) {
  return path
    .split('.')
    .map((part) => {
      if (part === NameToCodeMap.tech) {
        return
      }

      const allPossibleKeys = Object.keys(CodeToNameMap) as (keyof CodeToNameMap)[]
      const matchKey = allPossibleKeys.find((key) => key === part)

      return matchKey ? CodeToNameMap[matchKey] : part
    })
    .filter(Boolean)
    .join('_')
}
